import type { ReactNode } from "react";
import { useSession } from "./session";

export type NavItem = {
  id: string;
  label: string;
  href: string;
};

export type ShellLayoutProps = {
  /** Product / platform name shown in the header — not a Native-System brand. */
  title?: string;
  navItems?: NavItem[];
  /**
   * Optional Native-System-owned control rendered in the header, left of the
   * session chip (e.g. a language selector). Undefined by default — opt-in
   * per Native System, no visual change for one that doesn't pass it.
   */
  headerExtra?: ReactNode;
  children: ReactNode;
};

/**
 * Application frame: header, optional nav, main outlet.
 * Owns presentation chrome only (INV-PP-02 / AR-06).
 */
export function ShellLayout({
  title = "AxisNode",
  navItems = [],
  headerExtra,
  children,
}: ShellLayoutProps) {
  const { session } = useSession();

  return (
    <div
      data-testid="shell-layout"
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        background: "var(--ax-color-background)",
        color: "var(--ax-color-text)",
        fontFamily: "var(--ax-type-family-sans)",
      }}
    >
      <header data-testid="shell-header" className="ax-shell-header">
        <div className="ax-shell-header-left">
          <strong style={{ fontSize: "var(--ax-type-lg)" }}>{title}</strong>
          <nav aria-label="Primary" className="ax-shell-nav">
            {navItems.map((item) => (
              <a
                key={item.id}
                href={item.href}
                style={{ color: "inherit", textDecoration: "none", fontSize: "var(--ax-type-sm)" }}
              >
                {item.label}
              </a>
            ))}
          </nav>
        </div>
        <div className="ax-shell-header-right">
          {headerExtra}
          <div data-testid="shell-session-chip" className="ax-shell-session-chip">
            {session.status === "authenticated"
              ? session.email ?? session.subject ?? "Signed in"
              : "Signed out"}
          </div>
        </div>
      </header>
      <main data-testid="shell-main" className="ax-shell-main">
        {children}
      </main>
    </div>
  );
}
